import { useTranslation } from "react-i18next";
import { X } from "lucide-react";

import { ARTIFACT_TYPES, findModeConfig } from "~/shared/data/chat-models";
import { HEIGHT, SETTINGS, SPACE, TOUCH } from "~/shared/lib/design-tokens";
import { resolutionLabel } from "~/shared/lib/resolution";
import { cn } from "~/shared/lib/utils";
import { Button } from "~/shared/ui/button";

import { BatchSizeSection } from "./BatchSizeSection";
import { ModelSelector } from "./ModelSelector";
import { Section, TileBtn } from "./SettingsTiles";
import { TileSection } from "./TileSection";
import { useChatSettingsForm } from "./useChatSettingsForm";

interface SettingsPanelProps {
    onClose?: () => void;
    showClose?: boolean;
    className?: string;
}

export function SettingsPanel({ onClose, showClose = true, className }: SettingsPanelProps) {
    const { t } = useTranslation("chat-settings");
    const form = useChatSettingsForm();

    return (
        <div className={cn("flex min-h-0 flex-1 flex-col", className)}>
            <div className="flex shrink-0 items-center justify-between border-b border-white/5 px-4 py-3">
                <span className="text-sm font-medium text-zinc-200">{t("title")}</span>
                {showClose && onClose && (
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={onClose}
                        className={cn(TOUCH.tertiary, "text-zinc-500 hover:text-zinc-300")}
                    >
                        <X className="h-4 w-4" />
                    </Button>
                )}
            </div>

            <div className="flex-1 overflow-y-auto overscroll-contain">
                <div className={cn("p-4", SETTINGS.sectionGap)}>
                    <Section label={t("type")}>
                        <div className="flex gap-2">
                            {ARTIFACT_TYPES.map((option) => (
                                <button
                                    key={option.id}
                                    type="button"
                                    onClick={() => form.setArtifactType(option.id)}
                                    className={cn(
                                        HEIGHT.tile,
                                        "inline-flex flex-1 items-center justify-center rounded-lg text-sm font-medium",
                                        "[touch-action:manipulation] transition-colors duration-100 select-none",
                                        form.artifactType === option.id
                                            ? "bg-violet-500/20 text-violet-50 ring-1 ring-violet-400/50 ring-inset"
                                            : "text-zinc-500 hover:bg-white/5 hover:text-zinc-300",
                                    )}
                                >
                                    {t(`artifactType.${option.id}`)}
                                </button>
                            ))}
                        </div>
                    </Section>

                    <Section label={t("model")}>
                        <ModelSelector
                            models={form.availableModels}
                            loading={form.isLoadingModels}
                            selectedId={form.modelId}
                            onSelect={form.setModelId}
                        />
                    </Section>

                    {form.availableModes.length > 1 && (
                        <TileSection
                            label={t("mode")}
                            options={form.availableModes}
                            active={form.mode}
                            onSelect={form.setMode}
                            renderLabel={(m) => findModeConfig(form.currentModel, m)?.title ?? m}
                        />
                    )}

                    {form.availableAspectRatios.length > 0 && (
                        <TileSection
                            label={t("aspectRatio")}
                            options={form.availableAspectRatios}
                            active={form.aspectRatio}
                            onSelect={form.setAspectRatio}
                            renderLabel={(ar) => ar}
                        />
                    )}

                    {form.availableResolutions.length > 0 && (
                        <TileSection
                            label={t("resolution")}
                            options={form.availableResolutions}
                            active={form.resolution}
                            onSelect={form.setResolution}
                            renderLabel={resolutionLabel}
                        />
                    )}

                    {form.availableDurations.length > 0 && (
                        <TileSection
                            label={t("duration")}
                            options={form.availableDurations}
                            active={form.duration}
                            onSelect={form.setDuration}
                            renderLabel={(d) => `${d}s`}
                        />
                    )}

                    {form.availableUpscaleFactors.length > 0 && (
                        <TileSection
                            label={t("upscaleFactor")}
                            options={form.availableUpscaleFactors}
                            active={form.upscaleFactor ?? 0}
                            onSelect={form.setUpscaleFactor}
                            renderLabel={(factor) => `${factor}×`}
                        />
                    )}

                    {form.availableCharacterOrientations.length > 0 && (
                        <TileSection
                            label={t("characterOrientation")}
                            options={form.availableCharacterOrientations}
                            active={form.characterOrientation ?? ""}
                            onSelect={form.setCharacterOrientation}
                            renderLabel={(o) => t(`characterOrientationOption.${o}`)}
                        />
                    )}

                    {form.showAudio && (
                        <Section
                            label={t("audio")}
                            hint={form.audioLocked ? t("audioAlwaysHint") : undefined}
                        >
                            <div className={cn("flex", SPACE.tiles)}>
                                <TileBtn
                                    label={t("audioOn")}
                                    active={form.audioEnabled}
                                    disabled={form.audioLocked}
                                    title={form.audioLocked ? t("audioAlwaysHint") : undefined}
                                    onClick={() => form.setAudioEnabled(true)}
                                />
                                <TileBtn
                                    label={t("audioOff")}
                                    active={!form.audioEnabled}
                                    disabled={form.audioLocked}
                                    title={form.audioLocked ? t("audioAlwaysHint") : undefined}
                                    onClick={() => form.setAudioEnabled(false)}
                                />
                            </div>
                        </Section>
                    )}

                    {form.showBatchSize && (
                        <BatchSizeSection
                            batchSize={form.batchSize}
                            maxBatchSize={form.maxBatchSize}
                            onSelect={form.setBatchSize}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
